import { CSSProperties, memo } from 'react';
import { theme } from '@frontend/theme';
import { EVENT_Z_INDICES } from '../../constants';
import { useCalendarGridStore } from '../../store/calendar-grid.store';
import { CalendarEvent, CalendarEventType } from '../../types';
import { EventShell } from './event-shell';
import { AppointmentEvent } from './variants/appointment-event';
import { AvailabilitySlotEvent } from './variants/availability-slot-event';
import { AvailableBlockEvent } from './variants/available-block-event';
import { CancelledAppointmentEvent } from './variants/cancelled-appointment-event';
import { ExceptionEvent } from './variants/exception-event';
import { NonInteractiveSlotEvent } from './variants/non-interactive-slot';
import { ScheduleDefinitionEvent } from './variants/schedule-definition-event';
import { ScheduleRequestEvent } from './variants/schedule-request';
import { SlotStartLabelEvent } from './variants/slot-start-label';
import { UnavailableBlockEvent } from './variants/unavailable-block-event';

const EVENT_CSS_MAP: Record<CalendarEventType, CSSProperties> = {
  appointment: { zIndex: EVENT_Z_INDICES.appointment, margin: '1px 2px 1px 6px' },
  scheduleDefinition: { zIndex: EVENT_Z_INDICES.scheduleDefinition, margin: '0 0 1px 1px' },
  availabilitySlot: { zIndex: EVENT_Z_INDICES.availabilitySlot, margin: '0 0 1px 0' },
  availableBlock: { zIndex: EVENT_Z_INDICES.availableBlock },
  cancelledAppointment: { zIndex: EVENT_Z_INDICES.cancelledAppointment, margin: '1px 2px 1px 6px' },
  exception: { zIndex: EVENT_Z_INDICES.exception },
  scheduleRequest: { zIndex: EVENT_Z_INDICES.scheduleRequest, margin: '1px 2px 1px 6px' },
  unavailableBlock: { zIndex: EVENT_Z_INDICES.unavailableBlock },
  nonInteractiveSlot: { zIndex: EVENT_Z_INDICES.nonInteractiveSlot },
  slotsStartLabel: { zIndex: EVENT_Z_INDICES.slotsStartLabel },
};

const SELECTABLE_EVENT_TYPES = new Set<CalendarEventType>(['appointment', 'scheduleRequest', 'cancelledAppointment']);

const renderEvent = (eventInfo: CalendarEvent) => {
  switch (eventInfo.eventType) {
    case 'appointment':
      return <AppointmentEvent event={eventInfo} />;
    case 'scheduleDefinition':
      return <ScheduleDefinitionEvent event={eventInfo} />;
    case 'availabilitySlot':
      return <AvailabilitySlotEvent event={eventInfo} />;
    case 'availableBlock':
      return <AvailableBlockEvent event={eventInfo} />;
    case 'cancelledAppointment':
      return <CancelledAppointmentEvent event={eventInfo} />;
    case 'exception':
      return <ExceptionEvent event={eventInfo} />;
    case 'scheduleRequest':
      return <ScheduleRequestEvent event={eventInfo} />;
    case 'unavailableBlock':
      return <UnavailableBlockEvent event={eventInfo} />;
    case 'nonInteractiveSlot':
      return <NonInteractiveSlotEvent event={eventInfo} />;
    case 'slotsStartLabel':
      return <SlotStartLabelEvent event={eventInfo} />;
    default:
      return null;
  }
};

interface Props {
  eventId: string;
  style?: CSSProperties;
}

const EventRendererComponent = ({ eventId, style }: Props) => {
  const eventInfo = useCalendarGridStore((s) => s.eventsById[eventId]);
  const selectedEventId = useCalendarGridStore((s) => s.selectedEventId);
  const setSelectedEventId = useCalendarGridStore((s) => s.setSelectedEventId);
  const onEventSelect = useCalendarGridStore((s) => s.onEventSelect);
  if (!eventInfo) return null;

  const isSelectable = SELECTABLE_EVENT_TYPES.has(eventInfo.eventType);
  const isSelected = isSelectable && selectedEventId === eventInfo.id;

  const eventStyle = EVENT_CSS_MAP[eventInfo.eventType] || {};
  const selectedStyle = isSelected
    ? {
        outline: `2px solid ${theme.colors.primary50}`,
        outlineOffset: 2,
      }
    : {};
  const content = renderEvent(eventInfo);
  if (!content) return null;

  const handleSelect = isSelectable
    ? () => {
        setSelectedEventId(eventInfo.id);
        onEventSelect?.(eventInfo.id);
      }
    : undefined;

  return (
    <EventShell
      id={eventInfo.id}
      startTime={eventInfo.startTime}
      duration={eventInfo.duration}
      style={{ ...eventStyle, ...style, ...selectedStyle }}
      isSelectable={isSelectable}
      isSelected={isSelected}
      onSelect={handleSelect}
    >
      {content}
    </EventShell>
  );
};

export const EventRenderer = memo(EventRendererComponent);
